// src/components/superadmin/SuperAdminDashboard.js - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../contexts/AuthContext';
import CommissionSharing from './CommissionSharing';
import FairPlay from '../AdminDashboard/FairPlay';
import Dashboard from '../AdminDashboard/Dashboard';
import Promotions from '../AdminDashboard/Promotions';
import TournamentManagement from '../AdminDashboard/TournamentManagement';
import './SuperAdminDashboard.css';
import CreateTestUser from '../CreateTestUser';
import { canCreateTestUsers } from '../../config/environment';
import ChangePasswordModal from '../common/ChangePasswordModal';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));

// ===== Roles & Permissions (admin sub-accounts) =====
const ADMIN_CAPABILITIES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'users', label: 'Players' },
  { key: 'transactions', label: 'Transactions (reconciliation)' },
  { key: 'cash.initiate', label: 'Cash — Initiate injection/withdrawal' },
  { key: 'currency', label: 'Currency conversion' },
  { key: 'cash.approve1', label: 'Cash — 1st approval' },
  { key: 'tournament.approve', label: 'Private tournament approval' },
  { key: 'promotions', label: 'Promotions' },
  { key: 'cash.approve_final', label: 'Cash — Final approval' },
  { key: 'test_users', label: 'Test users' },
];

const ADMIN_PRESETS = {
  full: { label: 'Full Access (all tabs)', caps: ['full'] },
  finance: { label: 'Finance', caps: ['dashboard', 'users', 'transactions', 'cash.initiate', 'currency'] },
  marketing: { label: 'Marketing', caps: ['dashboard', 'users', 'cash.approve1', 'tournament.approve', 'promotions'] },
  approver: { label: 'Approver', caps: ['dashboard', 'users', 'tournament.approve', 'cash.approve_final'] },
  custom: { label: 'Custom (select tabs)', caps: [] },
};

const derivePreset = (perms) => {
  if (!Array.isArray(perms) || perms.length === 0 || perms.includes('full')) return 'full';
  const sorted = [...perms].sort().join(',');
  for (const key of ['finance', 'marketing', 'approver']) {
    if ([...ADMIN_PRESETS[key].caps].sort().join(',') === sorted) return key;
  }
  return 'custom';
};

const PermissionSelector = ({ value, onChange, disabled }) => {
  const perms = Array.isArray(value) && value.length ? value : ['full'];
  const [preset, setPreset] = useState(derivePreset(perms));
  const [caps, setCaps] = useState(perms.includes('full') ? ['full'] : [...perms]);

  const applyPreset = (key) => {
    setPreset(key);
    const next = key === 'full' ? ['full'] : (key === 'custom' ? [] : [...ADMIN_PRESETS[key].caps]);
    setCaps(next);
    onChange(next);
  };

  const toggle = (key, checked) => {
    const base = caps.filter((c) => c !== 'full');
    const next = checked ? [...base, key] : base.filter((c) => c !== key);
    setPreset('custom');
    setCaps(next);
    onChange(next);
  };

  return (
    <div className="form-section">
      <h4>Roles &amp; Permissions</h4>
      <div className="form-row">
        <div className="form-group">
          <label>Access Preset *</label>
          <select value={preset} onChange={(e) => applyPreset(e.target.value)} disabled={disabled}>
            {Object.entries(ADMIN_PRESETS).map(([k, p]) => (
              <option key={k} value={k}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row permission-checks">
        {ADMIN_CAPABILITIES.map((c) => {
          const checked = preset === 'full' || caps.includes(c.key);
          return (
            <label key={c.key} className="checkbox-item">
              <input type="checkbox" checked={checked} onChange={(e) => toggle(c.key, e.target.checked)} disabled={disabled || preset === 'full'} />
              {c.label}
            </label>
          );
        })}
      </div>
    </div>
  );
};

// Sub-accounts view — opens when a main account is clicked in the distributor list.
const SubAccountsView = ({ parent, subs, onBack, onEdit, onFund, onResetPin, onSuspend, onActivate }) => {
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
  return (
    <div className="section-card smart-table-card">
      <div className="card-header">
        <div className="distributor-header">
          <div className="header-left-section">
            <h2>Sub-admins of {parent.username}</h2>
            <button className="back-btn" onClick={onBack}>
              <i className="fas fa-arrow-left"></i> Back to Distributors
            </button>
          </div>
          <div className="distributor-count">
            <div className="count-number">{subs.length}</div>
            <div className="count-label">Sub-admins</div>
          </div>
        </div>
      </div>
      <div className="card-body">
        {subs.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-user-plus"></i>
            <h3>No Sub-admins</h3>
            <p>This main account has no sub-admins yet.</p>
          </div>
        ) : (
          <div className="smart-table-container">
            <table className="data-table transactions-table smart-table">
              <thead>
                <tr>
                  <th>Created Date</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Country</th>
                  <th className="text-right">Local Balance</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((admin) => (
                  <tr key={admin.id} className={!admin.is_active ? 'inactive-row' : ''}>
                    <td>{formatDate(admin.created_at)}</td>
                    <td>
                      <div className="username">{admin.username}</div>
                      <div className="user-id">ID: {admin.id}</div>
                    </td>
                    <td>{admin.email}</td>
                    <td>{admin.country}</td>
                    <td className="text-right">{admin.local_currency_symbol}{admin.local_balance.toFixed(2)}</td>
                    <td>
                      <span style={{ color: admin.is_active ? '#059669' : '#dc2626', fontWeight: 700 }}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="action-buttons">
                        <button className="action-btn edit" onClick={(e) => { e.stopPropagation(); onEdit(admin); }} title="Edit Distributor">
                          <i className="fas fa-edit"></i><span className="action-text">Edit</span>
                        </button>
                        <button className="action-btn fund" onClick={(e) => { e.stopPropagation(); onFund(admin); }} title="Fund Distributor">
                          <i className="fas fa-coins"></i><span className="action-text">Fund</span>
                        </button>
                        <button className="action-btn" onClick={(e) => { e.stopPropagation(); onResetPin(admin); }} title="Reset Security PIN" style={{ background: '#ff9800' }}>
                          <i className="fas fa-key"></i><span className="action-text">PIN</span>
                        </button>
                        {admin.is_active ? (
                          <button className="action-btn suspend" onClick={(e) => { e.stopPropagation(); onSuspend(admin); }} title="Suspend Distributor">
                            <i className="fas fa-ban"></i><span className="action-text">Suspend</span>
                          </button>
                        ) : (
                          <button className="action-btn activate" onClick={(e) => { e.stopPropagation(); onActivate(admin); }} title="Activate Distributor">
                            <i className="fas fa-check-circle"></i><span className="action-text">Activate</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [txnLoading, setTxnLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [fundingTarget, setFundingTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCountryTarget, setDeleteCountryTarget] = useState(null);
  const { logout, currentUser } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);

  // NEW STATES FOR EXCHANGE RATES
  const [exchangeRates, setExchangeRates] = useState([]);
  const [exchangeRatesLoading, setExchangeRatesLoading] = useState(false);
  const [editingRates, setEditingRates] = useState({});
  const [isUpdatingRates, setIsUpdatingRates] = useState(false);

  // NEW STATES FOR DEACTIVATION MODALS
  const [deactivateAdminModal, setDeactivateAdminModal] = useState(null);
  const [deactivateCountryModal, setDeactivateCountryModal] = useState(null);
  const [deactivationComment, setDeactivationComment] = useState('');
  const [showEditDistributorModal, setShowEditDistributorModal] = useState(false);
  const [editingDistributor, setEditingDistributor] = useState(null);
  const [viewingSubAccounts, setViewingSubAccounts] = useState(null);

  // Smart scroll states
  const [selectedRow, setSelectedRow] = useState(null);
  const [quickViewData, setQuickViewData] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);
  const [revealedPins, setRevealedPins] = useState({});

  // Pagination state for admins
  const [currentPage, setCurrentPage] = useState(1);
  const [currentCountryPage, setCurrentCountryPage] = useState(1);
  const [currentExchangeRatePage, setCurrentExchangeRatePage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCountryPages, setTotalCountryPages] = useState(1);
  const [totalExchangeRatePages, setTotalExchangeRatePages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [exchangeRateSearchTerm, setExchangeRateSearchTerm] = useState('');
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [totalCountries, setTotalCountries] = useState(0);
  const [totalExchangeRates, setTotalExchangeRates] = useState(0);

  // NEW: Show deactivated items
  const [showDeactivatedAdmins, setShowDeactivatedAdmins] = useState(false);
  const [showDeactivatedCountries, setShowDeactivatedCountries] = useState(false);

  const [filters, setFilters] = useState({
    type: '',
    username: '',
    email: '',
    currency: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  // Country form state
  const [countryForm, setCountryForm] = useState({
    country_name: '',
    country_code: '',
    currency_code: '',
    currency_name: '',
    currency_symbol: '',
    phone_extension: '',
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });

  // Distributor edit form state
  const [distributorEditForm, setDistributorEditForm] = useState({
    username: '',
    email: '',
    country_id: '',
    primary_phone: '',
    secondary_phone: '',
    bank_name: '',
    account_number: '',
    swift_code: '',
    mobile_money: '',
    gpay: '',
    other_payment_info: '',
    permissions: ['full']
  });

  // Refs for table containers
  const tableContainerRef = useRef(null);
  const transactionsTableRef = useRef(null);
  const countriesTableRef = useRef(null);
  const exchangeRatesTableRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // ==================== EXCHANGE RATES FUNCTIONS ====================

  // Fetch Exchange Rates
  const fetchExchangeRates = async () => {
    try {
      setExchangeRatesLoading(true);
      const res = await axios.get(`${API_BASE}/api/super-admin/exchange-rates`, getAuthHeader());
      
      if (res.data.success) {
        const ratesData = res.data.exchangeRates || [];
        console.log('Exchange Rates fetched:', ratesData);
        
        setExchangeRates(ratesData);
        setFilteredExchangeRates(ratesData);
        setTotalExchangeRates(ratesData.length);
        
        const pages = Math.ceil(ratesData.length / itemsPerPage);
        setTotalExchangeRatePages(pages || 1);
        
        // Initialize editing rates
        const initialEditingRates = {};
        ratesData.forEach(rate => {
          initialEditingRates[rate.id] = {
            usd_to_local_rate: rate.usd_to_local_rate || 0
          };
        });
        setEditingRates(initialEditingRates);
      } else {
        setExchangeRates([]);
        setFilteredExchangeRates([]);
        setTotalExchangeRates(0);
      }
    } catch (error) {
      console.error("Failed to fetch exchange rates", error);
      alert("Failed to load exchange rates. Check console for details.");
      setExchangeRates([]);
      setFilteredExchangeRates([]);
      setTotalExchangeRates(0);
    } finally {
      setExchangeRatesLoading(false);
    }
  };

  // Filtered exchange rates state
  const [filteredExchangeRates, setFilteredExchangeRates] = useState([]);

  // Handle exchange rate search
  const handleExchangeRateSearch = () => {
    let filtered = exchangeRates;

    if (!exchangeRateSearchTerm.trim()) {
      setFilteredExchangeRates(filtered);
      setTotalExchangeRatePages(Math.ceil(filtered.length / itemsPerPage));
      setCurrentExchangeRatePage(1);
      return;
    }

    const term = exchangeRateSearchTerm.toLowerCase().trim();
    filtered = filtered.filter(rate => 
      rate.country_name?.toLowerCase().includes(term) ||
      rate.country_code?.toLowerCase().includes(term) ||
      rate.currency_code?.toLowerCase().includes(term) ||
      rate.currency_name?.toLowerCase().includes(term)
    );
    
    setFilteredExchangeRates(filtered);
    setTotalExchangeRatePages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentExchangeRatePage(1);
  };

  // Clear exchange rate search
  const handleClearExchangeRateSearch = () => {
    setExchangeRateSearchTerm('');
    setFilteredExchangeRates(exchangeRates);
    setTotalExchangeRatePages(Math.ceil(exchangeRates.length / itemsPerPage));
    setCurrentExchangeRatePage(1);
  };

  // Get current exchange rates for pagination
  const getCurrentExchangeRates = () => {
    const startIndex = (currentExchangeRatePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredExchangeRates.slice(startIndex, endIndex);
  };

  // Exchange rate pagination controls
  const goToExchangeRatePage = (pageNumber) => {
    setCurrentExchangeRatePage(pageNumber);
  };

  const goToNextExchangeRatePage = () => {
    if (currentExchangeRatePage < totalExchangeRatePages) {
      setCurrentExchangeRatePage(prev => prev + 1);
    }
  };

  const goToPrevExchangeRatePage = () => {
    if (currentExchangeRatePage > 1) {
      setCurrentExchangeRatePage(prev => prev - 1);
    }
  };

  // Handle rate change
  const handleRateChange = (rateId, field, value) => {
    setEditingRates(prev => ({
      ...prev,
      [rateId]: {
        ...prev[rateId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Update all rates
  const updateAllRates = async () => {
    try {
      setIsUpdatingRates(true);
      
      // Prepare the rates data for update
      const ratesToUpdate = [];
      Object.keys(editingRates).forEach(rateId => {
        const rateData = editingRates[rateId];
        if (rateData) {
          ratesToUpdate.push({
            id: parseInt(rateId),
            rate: rateData.usd_to_local_rate || 0
          });
        }
      });

      console.log('Updating rates:', ratesToUpdate);
      
      const response = await axios.put(
        `${API_BASE}/api/super-admin/exchange-rates/bulk-update`,
        { rates: ratesToUpdate },
        getAuthHeader()
      );
      
      if (response.data.success) {
        alert('Exchange rates updated successfully!');
        fetchExchangeRates(); // Refresh the data
      } else {
        alert(response.data.message || 'Failed to update rates');
      }
    } catch (error) {
      console.error('Error updating exchange rates:', error);
      alert(error.response?.data?.message || 'Failed to update exchange rates');
    } finally {
      setIsUpdatingRates(false);
    }
  };

  // ==================== COUNTRY MANAGEMENT FUNCTIONS ====================

  // Fetch Countries - UPDATED to get ALL countries
 
const fetchCountries = async () => {
  try {
    setCountriesLoading(true);
    
    // Try the /countries/all endpoint FIRST to get ALL countries
    const res = await axios.get(`${API_BASE}/api/super-admin/countries/all`, getAuthHeader());
    console.log('Countries API Response (from /all):', res.data);
    
    if (res.data.success) {
      const countriesData = res.data.countries || [];
      console.log('Total countries fetched:', countriesData.length);
      console.log('Active countries:', countriesData.filter(c => c.is_active).length);
      console.log('Deactivated countries:', countriesData.filter(c => !c.is_active).length);
      
      setCountries(countriesData);
      
      // Filter based on active/deactivated toggle
      const filtered = countriesData.filter(country => 
        showDeactivatedCountries ? !country.is_active : country.is_active
      );
      setFilteredCountries(filtered);
      setTotalCountries(filtered.length);
      
      const pages = Math.ceil(filtered.length / itemsPerPage);
      setTotalCountryPages(pages || 1);
    } else {
      // If /all endpoint fails, try regular endpoint
      console.log('/all endpoint failed, trying regular endpoint...');
      const fallbackRes = await axios.get(`${API_BASE}/api/super-admin/countries`, getAuthHeader());
      
      if (fallbackRes.data.success) {
        const countriesData = fallbackRes.data.countries || [];
        setCountries(countriesData);
        
        const filtered = countriesData.filter(country => 
          showDeactivatedCountries ? !country.is_active : country.is_active
        );
        setFilteredCountries(filtered);
        setTotalCountries(filtered.length);
        
        const pages = Math.ceil(filtered.length / itemsPerPage);
        setTotalCountryPages(pages || 1);
      } else {
        setCountries([]);
        setFilteredCountries([]);
        setTotalCountries(0);
      }
    }
  } catch (error) {
    console.error("Failed to fetch countries from /all endpoint:", error);
    
    // Fallback to regular endpoint
    try {
      const fallbackRes = await axios.get(`${API_BASE}/api/super-admin/countries`, getAuthHeader());
      
      if (fallbackRes.data.success) {
        const countriesData = fallbackRes.data.countries || [];
        setCountries(countriesData);
        
        const filtered = countriesData.filter(country => 
          showDeactivatedCountries ? !country.is_active : country.is_active
        );
        setFilteredCountries(filtered);
        setTotalCountries(filtered.length);
        
        const pages = Math.ceil(filtered.length / itemsPerPage);
        setTotalCountryPages(pages || 1);
      } else {
        alert("Failed to load countries. Check console for details.");
        setCountries([]);
        setFilteredCountries([]);
        setTotalCountries(0);
      }
    } catch (fallbackError) {
      console.error("Both endpoints failed:", fallbackError);
      alert("Failed to load countries. Check console for details.");
      setCountries([]);
      setFilteredCountries([]);
      setTotalCountries(0);
    }
  } finally {
    setCountriesLoading(false);
  }
};

  // NEW: Toggle deactivated countries view
  const toggleDeactivatedCountries = () => {
    setShowDeactivatedCountries(!showDeactivatedCountries);
    setCurrentCountryPage(1);
  };

  // Handle country search - UPDATED for deactivated filter
  const handleCountrySearch = () => {
    let filtered = countries.filter(country => 
      showDeactivatedCountries ? !country.is_active : country.is_active
    );

    if (!countrySearchTerm.trim()) {
      setFilteredCountries(filtered);
      setTotalCountryPages(Math.ceil(filtered.length / itemsPerPage));
      setCurrentCountryPage(1);
      return;
    }

    const term = countrySearchTerm.toLowerCase().trim();
    filtered = filtered.filter(country => 
      country.country_name?.toLowerCase().includes(term) ||
      country.country_code?.toLowerCase().includes(term) ||
      country.currency_code?.toLowerCase().includes(term) ||
      country.currency_name?.toLowerCase().includes(term) ||
      country.phone_extension?.toString().includes(term)
    );
    
    setFilteredCountries(filtered);
    setTotalCountryPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentCountryPage(1);
  };

  // Clear country search
  const handleClearCountrySearch = () => {
    setCountrySearchTerm('');
    const filtered = countries.filter(country => 
      showDeactivatedCountries ? !country.is_active : country.is_active
    );
    setFilteredCountries(filtered);
    setTotalCountryPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentCountryPage(1);
  };

  // Get current countries for pagination
  const getCurrentCountries = () => {
    const startIndex = (currentCountryPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCountries.slice(startIndex, endIndex);
  };

  // Country pagination controls
  const goToCountryPage = (pageNumber) => {
    setCurrentCountryPage(pageNumber);
  };

  const goToNextCountryPage = () => {
    if (currentCountryPage < totalCountryPages) {
      setCurrentCountryPage(prev => prev + 1);
    }
  };

  const goToPrevCountryPage = () => {
    if (currentCountryPage > 1) {
      setCurrentCountryPage(prev => prev - 1);
    }
  };

  // Handle country form submit
  const handleCountrySubmit = async (e) => {
    e.preventDefault();
    
    if (!countryForm.country_name || !countryForm.country_code || 
        !countryForm.currency_code || !countryForm.currency_name) {
      alert('Please fill in all required fields: Country Name, Country Code, Currency Code, and Currency Name');
      return;
    }
    
    try {
      const countryData = {
        country_name: countryForm.country_name,
        country_code: countryForm.country_code.toUpperCase(),
        currency_code: countryForm.currency_code.toUpperCase(),
        currency_name: countryForm.currency_name,
        currency_symbol: countryForm.currency_symbol || '$',
        phone_extension: countryForm.phone_extension || '',
        is_active: countryForm.is_active
      };

      if (editingCountry) {
        await axios.put(
          `${API_BASE}/api/super-admin/countries/${editingCountry.id}`,
          countryData,
          getAuthHeader()
        );
        alert('Country updated successfully!');
      } else {
        await axios.post(
          `${API_BASE}/api/super-admin/countries`,
          countryData,
          getAuthHeader()
        );
        alert('Country created successfully!');
      }
      
      fetchCountries();
      setShowCountryModal(false);
      setEditingCountry(null);
      resetCountryForm();
    } catch (error) {
      console.error('Error saving country:', error);
      alert(error.response?.data?.message || 'Failed to save country. Check console for details.');
    }
  };

  // NEW: Handle country deactivation with comment
  const handleCountryDeactivate = async () => {
    if (!deactivateCountryModal) return;
    
    try {
      const response = await axios.put(
        `${API_BASE}/api/super-admin/toggle-country-status`,
        {
          countryId: deactivateCountryModal.id,
          isActive: false,
          comment: deactivationComment
        },
        getAuthHeader()
      );
      
      if (response.data.success) {
        setDeactivateCountryModal(null);
        setDeactivationComment('');
        fetchCountries();
        alert('Country deactivated successfully!');
      } else {
        alert(response.data.message || 'Failed to deactivate country');
      }
    } catch (error) {
      console.error('Deactivate country error:', error);
      alert("Failed to deactivate country: " + (error.response?.data?.message || error.message));
    }
  };

  // NEW: Handle country activation
  const handleCountryActivate = async (country) => {
    try {
      const response = await axios.put(
        `${API_BASE}/api/super-admin/toggle-country-status`,
        {
          countryId: country.id,
          isActive: true,
          comment: 'Reactivated by super admin'
        },
        getAuthHeader()
      );
      
      if (response.data.success) {
        fetchCountries();
        alert('Country activated successfully!');
      } else {
        alert(response.data.message || 'Failed to activate country');
      }
    } catch (error) {
      console.error('Activate country error:', error);
      alert("Failed to activate country: " + (error.response?.data?.message || error.message));
    }
  };

  // Handle country delete
  const handleCountryDeleteConfirm = async () => {
    if (!deleteCountryTarget) return;
    try {
      await axios.delete(
        `${API_BASE}/api/super-admin/countries/${deleteCountryTarget.id}`,
        getAuthHeader()
      );
      setDeleteCountryTarget(null);
      fetchCountries();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete country");
    }
  };

  // Reset country form
  const resetCountryForm = () => {
    setCountryForm({
      country_name: '',
      country_code: '',
      currency_code: '',
      currency_name: '',
      currency_symbol: '',
      phone_extension: '',
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
    setEditingCountry(null);
  };

  // Edit country
  const handleEditCountry = (country) => {
    setCountryForm({
      country_name: country.country_name || '',
      country_code: country.country_code || '',
      currency_code: country.currency_code || '',
      currency_name: country.currency_name || '',
      currency_symbol: country.currency_symbol || '',
      phone_extension: country.phone_extension || '',
      is_active: country.is_active !== undefined ? country.is_active : true,
      created_at: country.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
    setEditingCountry(country);
    setShowCountryModal(true);
  };

  // ==================== DISTRIBUTOR MANAGEMENT ====================

  // Fetch Admins/Distributors - UPDATED to include deactivated
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/super-admin/admins`, getAuthHeader());
      console.log('Distributors API Response:', res.data);
      
      if (res.data.success) {
        const adminsData = res.data.admins || [];
        
        const formattedAdmins = adminsData.map(admin => {
          // Calculate local balance based on country's currency
          let localBalance = 0;
          let localCurrency = admin.currency_code || admin.default_currency || 'USD';
          let localCurrencySymbol = admin.currency_symbol || '$';
          
          // Determine which wallet column to use based on currency
          switch(localCurrency) {
            case 'INR':
              localBalance = parseFloat(admin.wallet_balance_inr || 0);
              localCurrencySymbol = admin.currency_symbol || '₹';
              break;
            case 'LRD':
              localBalance = parseFloat(admin.wallet_balance_lrd || 0);
              localCurrencySymbol = admin.currency_symbol || 'L$';
              break;
            case 'USD':
              localBalance = parseFloat(admin.wallet_balance_usd || 0);
              localCurrencySymbol = admin.currency_symbol || '$';
              break;
            case 'XOF':
              localBalance = parseFloat(admin.wallet_balance_lrd || 0); // XOF stored in USD column
              localCurrencySymbol = admin.currency_symbol || 'CFA';
              break;
            default:
              // For other currencies, use USD column
              localBalance = parseFloat(admin.wallet_balance_lrd || 0);
              localCurrencySymbol = admin.currency_symbol || '$';
          }
          
          return {
            id: admin.id || '',
            username: admin.username || 'N/A',
            email: admin.email || 'N/A',
            is_active: admin.is_active !== undefined ? admin.is_active : true,
            wallet_balance_inr: parseFloat(admin.wallet_balance_inr || 0),
            wallet_balance_usd: parseFloat(admin.wallet_balance_usd || 0),
            wallet_balance_lrd: parseFloat(admin.wallet_balance_lrd || 0),
            created_at: admin.created_at || new Date().toISOString(),
            last_login: admin.last_login || null,
            admin_pin: admin.admin_pin || '',
            permissions: admin.permissions || ['full'],
            parent_admin_id: admin.parent_admin_id || null,
            country: admin.country_name || 'N/A',
            country_id: admin.country_id || '',
            country_code: admin.country_code || '',
            primary_phone: admin.primary_phone || '',
            secondary_phone: admin.secondary_phone || '',
            bank_name: admin.bank_name || '',
            account_number: admin.account_number || '',
            swift_code: admin.swift_code || '',
            mobile_money: admin.mobile_money || '',
            gpay: admin.gpay || '',
            other_payment_info: admin.other_payment_info || '',
            phone_extension: admin.phone_extension || '',
            currency_code: admin.currency_code || admin.default_currency || 'USD',
            currency_name: admin.currency_name || 'US Dollar',
            currency_symbol: localCurrencySymbol,
            // For display purposes
            local_balance: localBalance,
            local_currency: localCurrency,
            local_currency_symbol: localCurrencySymbol
          };
        });
        
        // Order: main accounts first, then their sub-accounts grouped beneath them.
        const mainAccounts = formattedAdmins.filter(a => !a.parent_admin_id);
        const subAccounts = formattedAdmins.filter(a => a.parent_admin_id);
        const groupedAdmins = [];
        mainAccounts.forEach(main => {
          groupedAdmins.push(main);
          subAccounts
            .filter(s => String(s.parent_admin_id) === String(main.id))
            .forEach(s => groupedAdmins.push(s));
        });
        subAccounts
          .filter(s => !mainAccounts.some(m => String(m.id) === String(s.parent_admin_id)))
          .forEach(s => groupedAdmins.push(s));

        setAdmins(groupedAdmins);
        
        // Filter based on active/deactivated toggle
        // Filter based on active/deactivated toggle — main accounts only (sub-accounts are
        // shown in a dedicated sub-accounts view opened from each main account).
        const filtered = groupedAdmins.filter(admin => 
          !admin.parent_admin_id && (showDeactivatedAdmins ? !admin.is_active : admin.is_active)
        );
        setFilteredAdmins(filtered);
        setTotalAdmins(filtered.length);
        
        const pages = Math.ceil(filtered.length / itemsPerPage);
        setTotalPages(pages || 1);
        
        console.log('Formatted Distributors:', formattedAdmins);
      } else {
        console.error('API Error:', res.data.message);
        setAdmins([]);
        setFilteredAdmins([]);
        setTotalAdmins(0);
      }
    } catch (error) {
      console.error("Failed to fetch distributors", error);
      alert("Failed to load distributors. Check console for details.");
      setAdmins([]);
      setFilteredAdmins([]);
      setTotalAdmins(0);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Toggle deactivated admins view
  const toggleDeactivatedAdmins = () => {
    setShowDeactivatedAdmins(!showDeactivatedAdmins);
    setCurrentPage(1);
  };

  // Search functionality for distributors - UPDATED for deactivated filter
  const handleSearch = () => {
    let filtered = admins.filter(admin => 
      !admin.parent_admin_id && (showDeactivatedAdmins ? !admin.is_active : admin.is_active)
    );

    if (!searchTerm.trim()) {
      setFilteredAdmins(filtered);
      setTotalPages(Math.ceil(filtered.length / itemsPerPage));
      setCurrentPage(1);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    filtered = filtered.filter(admin => 
      admin.username.toLowerCase().includes(term) ||
      admin.email.toLowerCase().includes(term) ||
      admin.id.toString().includes(term) ||
      (admin.country && admin.country.toLowerCase().includes(term)) ||
      (admin.primary_phone && admin.primary_phone.toString().includes(term))
    );
    
    setFilteredAdmins(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentPage(1);
  };

  // Handle Enter key press for search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search - UPDATED for deactivated filter
  const handleClearSearch = () => {
    setSearchTerm('');
    const filtered = admins.filter(admin => 
      !admin.parent_admin_id && (showDeactivatedAdmins ? !admin.is_active : admin.is_active)
    );
    setFilteredAdmins(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentPage(1);
  };

  // Get current distributors for pagination
  const getCurrentAdmins = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAdmins.slice(startIndex, endIndex);
  };

  // Pagination controls
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Fetch Transactions
  const fetchTransactions = async () => {
    try {
      setTxnLoading(true);
      const params = new URLSearchParams(filters);
      const res = await axios.get(
        `${API_BASE}/api/super-admin/transactions?${params}`,
        getAuthHeader()
      );
      
      if (res.data.success && res.data.transactions) {
        const formattedTransactions = res.data.transactions.map(tx => ({
          id: tx.id || '',
          user_id: tx.user_id || '',
          username: tx.username || '',
          user_email: tx.user_email || '',
          type: tx.type || '',
          currency: tx.currency || 'INR',
          amount: parseFloat(tx.amount || 0),
          opening_balance: parseFloat(tx.opening_balance || 0),
          closing_balance: parseFloat(tx.closing_balance || 0),
          status: tx.status || '',
          description: tx.description || '',
          reference_number: tx.reference_number || `TX${String(tx.id || '').padStart(6, '0')}`,
          performed_by_username: tx.performed_by_username || 'System',
          created_at: tx.created_at || new Date().toISOString()
        }));
        setTransactions(formattedTransactions);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error("Failed to fetch transactions", error);
      setTransactions([]);
    } finally {
      setTxnLoading(false);
    }
  };

  // NEW: Handle distributor deactivation with comment
  const handleAdminDeactivate = async () => {
    if (!deactivateAdminModal) return;
    
    try {
      const response = await axios.put(
        `${API_BASE}/api/super-admin/update-admin-status`,
        {
          adminId: deactivateAdminModal.id,
          isActive: false,
          comment: deactivationComment
        },
        getAuthHeader()
      );
      
      if (response.data.success) {
        setDeactivateAdminModal(null);
        setDeactivationComment('');
        fetchAdmins();
        alert('Distributor suspended successfully!');
      } else {
        alert(response.data.message || 'Failed to suspend distributor');
      }
    } catch (error) {
      console.error('Deactivate admin error:', error);
      alert("Failed to suspend distributor: " + (error.response?.data?.message || error.message));
    }
  };

  // NEW: Handle distributor activation
  const handleAdminActivate = async (admin) => {
    try {
      const response = await axios.put(
        `${API_BASE}/api/super-admin/update-admin-status`,
        {
          adminId: admin.id,
          isActive: true,
          comment: 'Reactivated by super admin'
        },
        getAuthHeader()
      );
      
      if (response.data.success) {
        fetchAdmins();
        alert('Distributor activated successfully!');
      } else {
        alert(response.data.message || 'Failed to activate distributor');
      }
    } catch (error) {
      console.error('Activate admin error:', error);
      alert("Failed to activate distributor: " + (error.response?.data?.message || error.message));
    }
  };

  // NEW: Handle distributor edit
  const handleEditDistributor = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.put(
        `${API_BASE}/api/super-admin/update-admin/${editingDistributor.id}`,
        distributorEditForm,
        getAuthHeader()
      );
      
      if (response.data.success) {
        alert('Distributor updated successfully!');
        setShowEditDistributorModal(false);
        setEditingDistributor(null);
        fetchAdmins();
      } else {
        alert(response.data.message || 'Failed to update distributor');
      }
    } catch (error) {
      console.error('Error updating distributor:', error);
      alert(error.response?.data?.message || 'Failed to update distributor');
    }
  };

  // NEW: Open edit distributor modal
  const openEditDistributorModal = (admin) => {
    setEditingDistributor(admin);
    setDistributorEditForm({
      username: admin.username,
      email: admin.email,
      country_id: admin.country_id,
      primary_phone: admin.primary_phone,
      secondary_phone: admin.secondary_phone,
      bank_name: admin.bank_name,
      account_number: admin.account_number,
      swift_code: admin.swift_code,
      mobile_money: admin.mobile_money,
      gpay: admin.gpay,
      other_payment_info: admin.other_payment_info,
      permissions: admin.permissions || ['full']
    });
    setShowEditDistributorModal(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = () => {
    fetchTransactions();
  };

  const handleResetFilters = () => {
    setFilters({
      type: '',
      username: '',
      email: '',
      currency: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    setTimeout(() => fetchTransactions(), 100);
  };

  const handleExport = async (format) => {
    // Dashboard tab → export analytics (graphs + data)
    if (activeTab === 'dashboard') {
      exportDashboardData(format);
      return;
    }

    // Transactions tab → export filtered transactions
    try {
      const params = new URLSearchParams(filters);
      const response = await axios.get(
        `${API_BASE}/api/super-admin/export-transactions/${format}?${params}`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );

      const ext = format === 'excel' ? 'xlsx' : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      alert("Failed to export transactions");
    }
  };

  const downloadText = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const renderChartToCanvas = (data, valueKey, color, title) => {
    const width = 900;
    const height = 340;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Title
    ctx.fillStyle = '#111827';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(title, 46, 26);

    const values = (data || []).map(d => Number(d[valueKey]) || 0);
    const max = Math.max(1, ...values);
    const pad = { top: 42, bottom: 52, left: 46, right: 12 };
    const chartW = width - pad.left - pad.right;
    const chartH = height - pad.top - pad.bottom;
    const barW = values.length ? chartW / values.length : 0;

    // Baseline
    ctx.strokeStyle = '#e5e7eb';
    ctx.beginPath();
    ctx.moveTo(pad.left, pad.top + chartH);
    ctx.lineTo(pad.left + chartW, pad.top + chartH);
    ctx.stroke();

    values.forEach((v, i) => {
      const h = Math.round((v / max) * chartH);
      const x = pad.left + i * barW;
      const y = pad.top + chartH - h;

      ctx.fillStyle = color;
      ctx.fillRect(x + 1, y, Math.max(1, barW - 2), h);

      // Value label (only when there are few bars)
      if (values.length <= 12 && v > 0) {
        ctx.fillStyle = '#374151';
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(v), x + barW / 2, y - 4);
      }

      // Date label
      const label = (data[i] && data[i].date) ? data[i].date.slice(5) : '';
      ctx.fillStyle = '#6b7280';
      ctx.font = '10px sans-serif';
      ctx.save();
      ctx.translate(x + barW / 2, pad.top + chartH + 12);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.fillText(label, 0, 0);
      ctx.restore();
    });

    return canvas.toDataURL('image/png');
  };

  const exportDashboardData = (format) => {
    const d = dashboardData || {};
    const a = d.activeUsers || {};
    const u = d.usage || {};
    const wb = d.totalWalletBalance ?? 0;
    const date = new Date().toISOString().split('T')[0];

    const summary = [
      ['Metric', 'Value'],
      ['Total Active Customers', a.total ?? 0],
      ['Active Customers DoD (%)', a.dod ?? 0],
      ['Active Customers MoM (%)', a.mom ?? 0],
      ['Active Customers YoY (%)', a.yoy ?? 0],
      ['Total Customer Usage (USD)', (u.total ?? 0).toFixed(2)],
      ['Usage DoD (%)', u.dod ?? 0],
      ['Usage MoM (%)', u.mom ?? 0],
      ['Usage YoY (%)', u.yoy ?? 0],
      ['Total Players Wallet Balance (USD)', wb.toFixed(2)]
    ];

    const dailyMap = {};
    (a.daily || []).forEach(x => { dailyMap[x.date] = dailyMap[x.date] || {}; dailyMap[x.date].count = x.count; });
    (u.daily || []).forEach(x => { dailyMap[x.date] = dailyMap[x.date] || {}; dailyMap[x.date].amount = x.amount; });
    const dailyRows = Object.keys(dailyMap).sort().map(day => ({
      date: day,
      active: dailyMap[day].count ?? 0,
      usage: dailyMap[day].amount ?? 0
    }));

    if (format === 'csv') {
      const lines = [];
      lines.push('Metric,Value');
      summary.slice(1).forEach(r => lines.push(`"${r[0]}","${r[1]}"`));
      lines.push('');
      lines.push('Date,Active Customers,Usage (USD)');
      dailyRows.forEach(r => lines.push(`${r.date},${r.active},${r.usage}`));
      downloadText('\uFEFF' + lines.join('\n'), `dashboard_${date}.csv`, 'text/csv;charset=utf-8');
    } else if (format === 'excel') {
      const ws1 = XLSX.utils.aoa_to_sheet(summary);
      const ws2 = XLSX.utils.aoa_to_sheet([
        ['Date', 'Active Customers', 'Usage (USD)'],
        ...dailyRows.map(r => [r.date, r.active, r.usage])
      ]);
      const wb2 = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb2, ws1, 'Summary');
      XLSX.utils.book_append_sheet(wb2, ws2, 'Daily');
      XLSX.writeFile(wb2, `dashboard_${date}.xlsx`);
    } else if (format === 'pdf') {
      const chartActive = renderChartToCanvas(a.daily, 'count', '#4f46e5', 'Daily Active Customers');
      const chartUsage = renderChartToCanvas(u.daily, 'amount', '#10b981', 'Daily Customer Usage (USD)');

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.setTextColor(17, 24, 39);
      doc.text('Dashboard Analytics', 14, 16);
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

      const ensureSpace = (needed) => {
        if (y + needed > 280) {
          doc.addPage();
          y = 16;
        }
      };

      let y = 28;
      autoTable(doc, {
        startY: y,
        head: [['Metric', 'Value']],
        body: summary.slice(1).map(r => [r[0], String(r[1])])
      });
      y = doc.lastAutoTable.finalY + 14;

      ensureSpace(74);
      doc.setFontSize(13);
      doc.setTextColor(17, 24, 39);
      doc.text('Daily Active Customers', 14, y);
      y += 5;
      doc.addImage(chartActive, 'PNG', 14, y, 182, 68);
      y += 74;

      ensureSpace(74);
      doc.setFontSize(13);
      doc.setTextColor(17, 24, 39);
      doc.text('Daily Customer Usage (USD)', 14, y);
      y += 5;
      doc.addImage(chartUsage, 'PNG', 14, y, 182, 68);
      y += 74;

      autoTable(doc, {
        startY: y,
        head: [['Date', 'Active Customers', 'Usage (USD)']],
        body: dailyRows.map(r => [r.date, String(r.active), String(r.usage)])
      });
      doc.save(`dashboard_${date}.pdf`);
    }
  };

  // Show quick details
  const showQuickDetails = (admin) => {
    setSelectedRow(admin.id);
    setQuickViewData(admin);
    setShowQuickView(true);
  };

  const handleResetPin = async (admin) => {
    if (!window.confirm(`Reset the security PIN for ${admin.username}?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/super-admin/admin/${admin.id}/reset-pin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        alert(`New PIN for ${admin.username}: ${res.data.admin.admin_pin}`);
        fetchAdmins();
      } else {
        alert(res.data.message || 'Failed to reset PIN');
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to reset PIN');
    }
  };

   useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdmins();
    } else if (activeTab === 'transactions') {
      fetchTransactions();
    } else if (activeTab === 'countries') {
      fetchCountries();
    } else if (activeTab === 'exchangeRates') {
      fetchExchangeRates();
    }
  }, [activeTab, showDeactivatedAdmins, showDeactivatedCountries]);

  // Load countries on mount so the "Create Distributor" dropdown is always populated
  useEffect(() => {
    fetchCountries();
  }, []);

  // Reset to page 1 when search results change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredAdmins]);

  useEffect(() => {
    setCurrentCountryPage(1);
  }, [filteredCountries]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbols = { 
      INR: '₹', 
      USD: '$', 
      LRD: 'L$',
      XOF: 'CFA',
      NGN: '₦',
      GHS: 'GH₵',
      KES: 'KSh'
    };
    const symbol = symbols[currency] || '';
    return `${symbol}${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatCurrencyValue = (value) => {
    return parseFloat(value || 0).toFixed(2);
  };

 return (
    <div className="super-admin-dashboard">
      {/* HEADER - UNCHANGED */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo">
            <i className="fas fa-shield-alt"></i>
            <span className="logo-text">Super Admin Portal</span>
          </div>
        </div>
        <div className="header-right">
          <div className="user-info">
            <div className="avatar">
              {currentUser?.username?.[0]?.toUpperCase() || 'S'}
            </div>
            <div className="user-details">
              <div className="user-name">{currentUser?.username || 'Super Admin'}</div>
              <div className="user-role">Super Administrator</div>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="logout-btn"
              style={{ background: '#0e6b45' }}
              onClick={() => setShowChangePassword(true)}
            >
              <i className="fas fa-key"></i>
              <span>Change Password</span>
            </button>
            <button className="logout-btn" onClick={logout}>
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

        {/* MAIN CONTAINER */}
      <div className="dashboard-container">
        {/* SIDEBAR - UPDATED WITH EXCHANGE RATES */}
        <div className="sidebar">
          <div className="sidebar-menu">
            <div
              className={`menu-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <div className="menu-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Dashboard</div>
              </div>
            </div>
            <div 
              className={`menu-item ${activeTab === 'admins' ? 'active' : ''}`}
              onClick={() => setActiveTab('admins')}
            >
              <div className="menu-icon">
                <i className="fas fa-user-tie"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Distributor Management</div>
              </div>
            </div>

            <div 
              className={`menu-item ${activeTab === 'countries' ? 'active' : ''}`}
              onClick={() => setActiveTab('countries')}
            >
              <div className="menu-icon">
                <i className="fas fa-globe"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Country Management</div>
              </div>
            </div>

            {/* NEW: Exchange Rates Menu Item */}
            <div 
              className={`menu-item ${activeTab === 'exchangeRates' ? 'active' : ''}`}
              onClick={() => setActiveTab('exchangeRates')}
            >
              <div className="menu-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Exchange Rates</div>
              </div>
            </div>

            <div 
              className={`menu-item ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              <div className="menu-icon">
                <i className="fas fa-exchange-alt"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Transaction History</div>
              </div>
            </div>

            <div
              className={`menu-item ${activeTab === 'commission' ? 'active' : ''}`}
              onClick={() => setActiveTab('commission')}
            >
              <div className="menu-icon">
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Commission Sharing</div>
              </div>
            </div>

            <div
              className={`menu-item ${activeTab === 'fairPlay' ? 'active' : ''}`}
              onClick={() => setActiveTab('fairPlay')}
            >
              <div className="menu-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Fair Play</div>
              </div>
            </div>

            <div
              className={`menu-item ${activeTab === 'promotions' ? 'active' : ''}`}
              onClick={() => setActiveTab('promotions')}
            >
              <div className="menu-icon">
                <i className="fas fa-gift"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Promotions</div>
              </div>
            </div>

            <div
              className={`menu-item ${activeTab === 'tournaments' ? 'active' : ''}`}
              onClick={() => setActiveTab('tournaments')}
            >
              <div className="menu-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="menu-text">
                <div className="menu-title">Tournaments</div>
              </div>
            </div>

            {canCreateTestUsers() && (
              <div
                className={`menu-item ${activeTab === 'testUsers' ? 'active' : ''}`}
                onClick={() => setActiveTab('testUsers')}
              >
                <div className="menu-icon">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div className="menu-text">
                  <div className="menu-title">Create Test User</div>
                </div>
              </div>
            )}
          </div>
        </div>

          {/* MAIN CONTENT */}
        <div className="main-content">
          {/* PAGE HEADER - UPDATED FOR EXCHANGE RATES */}
          <div className="page-header">
            <div className="page-title">
              <h1>
                {activeTab === 'dashboard' ? 'Dashboard' :
                 activeTab === 'admins' ? 'Distributor Management' : 
                 activeTab === 'countries' ? 'Country Management' : 
                 activeTab === 'exchangeRates' ? 'Exchange Rates Management' :
                 activeTab === 'fairPlay' ? 'Fair Play Watch Dog' :
                 activeTab === 'promotions' ? 'Promotions' :
                 activeTab === 'commission' ? 'Commission Sharing' :
                 activeTab === 'tournaments' ? 'Tournament Management' :
                 activeTab === 'testUsers' ? 'Create Test User' :
                 'Transaction History'}
              </h1>
              {activeTab !== 'dashboard' && activeTab !== 'commission' && activeTab !== 'promotions' && activeTab !== 'testUsers' && (
                <p className="page-subtitle">
                  {activeTab === 'admins' 
                    ? 'Create, manage, and monitor distributor accounts' 
                    : activeTab === 'countries'
                    ? 'Manage countries, currencies, and regional settings'
                    : activeTab === 'exchangeRates'
                    ? 'Manage and update currency exchange rates for all countries'
                    : 'View, filter, and export all system transactions'}
                </p>
              )}
            </div>
            {activeTab === 'admins' ? (
              <button className="create-btn" onClick={() => setShowCreateModal(true)}>
                <i className="fas fa-plus"></i>
                <span>Create New Distributor</span>
              </button>
            ) : activeTab === 'countries' ? (
              <button className="create-btn" onClick={() => setShowCountryModal(true)}>
                <i className="fas fa-plus"></i>
                <span>Add New Country</span>
              </button>
            ) : activeTab === 'exchangeRates' ? (
              <button 
                className="create-btn" 
                onClick={updateAllRates}
                disabled={isUpdatingRates || exchangeRatesLoading}
              >
                {isUpdatingRates ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    <span>Update All Rates</span>
                  </>
                )}
              </button>
            ) : activeTab === 'commission' || activeTab === 'promotions' || activeTab === 'fairPlay' || activeTab === 'tournaments' || activeTab === 'testUsers' ? null : (
              <div className="export-buttons">
                <button className="export-btn excel" onClick={() => handleExport('excel')}>
                  <i className="fas fa-file-excel"></i>
                  <span>Excel</span>
                </button>
                <button className="export-btn pdf" onClick={() => handleExport('pdf')}>
                  <i className="fas fa-file-pdf"></i>
                  <span>PDF</span>
                </button>
                <button className="export-btn csv" onClick={() => handleExport('csv')}>
                  <i className="fas fa-file-csv"></i>
                  <span>CSV</span>
                </button>
              </div>
            )}
          </div>


          {/* CONTENT AREA */}
          <div className="content-area">
            {activeTab === 'dashboard' ? (
              <Dashboard global onDataChange={setDashboardData} />
            ) : activeTab === 'admins' ? (
              <div className="admins-section">
                {viewingSubAccounts ? (
                  <SubAccountsView
                    parent={viewingSubAccounts}
                    subs={admins.filter(a => String(a.parent_admin_id) === String(viewingSubAccounts.id))}
                    onBack={() => setViewingSubAccounts(null)}
                    onEdit={openEditDistributorModal}
                    onFund={(a) => setFundingTarget(a)}
                    onResetPin={handleResetPin}
                    onSuspend={(a) => { setDeactivateAdminModal(a); setDeactivationComment(''); }}
                    onActivate={handleAdminActivate}
                  />
                ) : (
                <div className="section-card smart-table-card">
                  <div className="card-header">
                    <div className="distributor-header">
                      <div className="header-left-section">
                        <h2>{showDeactivatedAdmins ? 'Deactivated Distributors' : 'All Distributors'}</h2>
                        {/* NEW: Toggle for showing deactivated */}
                        <div className="status-toggle">
                          <button 
                            className={`toggle-btn ${!showDeactivatedAdmins ? 'active' : ''}`}
                            onClick={() => setShowDeactivatedAdmins(false)}
                          >
                            Active ({admins.filter(a => a.is_active).length})
                          </button>
                          <button 
                            className={`toggle-btn ${showDeactivatedAdmins ? 'active' : ''}`}
                            onClick={() => setShowDeactivatedAdmins(true)}
                          >
                            Deactivated ({admins.filter(a => !a.is_active).length})
                          </button>
                        </div>
                      </div>
                      
                      <div className="header-center-section">
                        <div className="search-section">
                          <div className="search-input-group">
                            <i className="fas fa-search search-icon"></i>
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search username, email, country or phone..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              onKeyPress={handleKeyPress}
                            />
                            {searchTerm && (
                              <button className="clear-search-btn" onClick={handleClearSearch}>
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </div>
                          <button className="search-btn" onClick={handleSearch}>
                            <i className="fas fa-search"></i>
                            <span>Search</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="header-right-section">
                        <div className="distributor-count">
                          <div className="count-icon">
                            <i className="fas fa-users"></i>
                          </div>
                          <div className="count-details">
                            <div className="count-number">{totalAdmins}</div>
                            <div className="count-label">
                              {showDeactivatedAdmins ? 'Deactivated' : 'Active'} Distributors
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    {loading ? (
                      <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Loading distributors...</span>
                      </div>
                    ) : filteredAdmins.length > 0 ? (
                      <>
                        <div className="smart-table-container" ref={tableContainerRef}>
                          <table className="data-table transactions-table smart-table">
                            <thead>
                              <tr>
                                <th className="sticky-col first-col">Created Date</th>
                                <th className="sticky-col second-col">Username</th>
                                <th>Email</th>
                                <th>Country</th>
                                <th>Primary Phone</th>
                                <th className="text-right">USD Balance</th>
                                <th className="text-right">INR Balance</th>
                                <th className="text-right">Local Balance</th>
                                <th>Status</th>
                                <th className="text-center sticky-actions">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getCurrentAdmins().map((admin) => (
                                <tr 
                                  key={admin.id} 
                                  className={`${!admin.is_active ? 'inactive-row' : ''} ${selectedRow === admin.id ? 'selected' : ''}`}
                                  onClick={() => showQuickDetails(admin)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td className="sticky-col first-col">
                                    <div className="date-cell">
                                      {formatDate(admin.created_at)}
                                    </div>
                                  </td>
                                  <td className="sticky-col second-col">
                                    <div className="user-cell">
                                      <div className="user-avatar">
                                        {admin.username?.[0]?.toUpperCase() || 'A'}
                                      </div>
                                      <div className="user-info-cell">
                                        <div className="username">{admin.username}</div>
                                        {admin.parent_admin_id && (
                                          <div className="sub-account-badge">
                                            <i className="fas fa-level-up-alt fa-rotate-90"></i>{' '}
                                            Sub-account of {admins.find(a => String(a.id) === String(admin.parent_admin_id))?.username || admin.parent_admin_id}
                                          </div>
                                        )}
                                        <div className="user-id">ID: {admin.id}</div>
                                        <div className="user-id" style={{ color: '#ffd54f', display: 'flex', alignItems: 'center' }}>
                                          <span>PIN: {admin.admin_pin ? (revealedPins[admin.id] ? admin.admin_pin : '••••••') : '—'}</span>
                                          {admin.admin_pin && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); setRevealedPins((p) => ({ ...p, [admin.id]: !p[admin.id] })); }}
                                              style={{ marginLeft: '6px', cursor: 'pointer', background: 'transparent', border: 'none', color: '#ffd54f', fontSize: '12px' }}
                                              title={revealedPins[admin.id] ? 'Hide PIN' : 'Reveal PIN'}
                                            >
                                              {revealedPins[admin.id] ? '🙈' : '👁️'}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="email-cell">
                                      {admin.email}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="country-cell">
                                      <span className={`flag-icon flag-${admin.country_code?.toLowerCase() || 'us'}`}></span>
                                      <span className="country-name">{admin.country || 'N/A'}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="phone-cell">
                                      {admin.primary_phone || 'N/A'}
                                      {admin.secondary_phone && (
                                        <div className="secondary-phone">
                                          {admin.secondary_phone}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="text-right">
                                    <div className="currency-cell">
                                      <span className="currency-symbol">$</span>
                                      <span className="amount">{admin.wallet_balance_usd.toFixed(2)}</span>
                                      <div className="currency-name-small">USD</div>
                                    </div>
                                  </td>
                                  <td className="text-right">
                                    <div className="currency-cell">
                                      <span className="currency-symbol">₹</span>
                                      <span className="amount">{admin.wallet_balance_inr.toFixed(2)}</span>
                                      <div className="currency-name-small">INR</div>
                                    </div>
                                  </td>
                                  <td className="text-right">
                                    <div className="currency-cell local">
                                      <span className="currency-symbol">
                                        {admin.local_currency_symbol}
                                      </span>
                                      <span className="amount">
                                        {admin.local_balance.toFixed(2)}
                                      </span>
                                      <div className="currency-name-small">
                                        {admin.local_currency}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className={`status-cell ${admin.is_active ? 'active' : 'suspended'}`}>
                                      <div className="status-dot"></div>
                                      <span>{admin.is_active ? 'Active' : 'Suspended'}</span>
                                    </div>
                                  </td>
                                  <td className="text-center sticky-actions">
                                    <div className="action-buttons">
                                      {/* CHANGED: View button to Edit button */}
                                      <button 
                                        className="action-btn edit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditDistributorModal(admin);
                                        }}
                                        title="Edit Distributor"
                                      >
                                        <i className="fas fa-edit"></i>
                                        <span className="action-text">Edit</span>
                                      </button>
                                      <button 
                                        className="action-btn view-subs"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setViewingSubAccounts(admin);
                                        }}
                                        title="View Sub-admins"
                                        style={{ background: '#7c3aed' }}
                                      >
                                        <i className="fas fa-users-cog"></i>
                                        <span className="action-text">Sub-admins</span>
                                      </button>
                                      <button 
                                        className="action-btn fund"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFundingTarget(admin);
                                        }}
                                        title="Fund Distributor"
                                      >
                                        <i className="fas fa-coins"></i>
                                        <span className="action-text">Fund</span>
                                      </button>
                                      <button 
                                        className="action-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleResetPin(admin);
                                        }}
                                        title="Reset Security PIN"
                                        style={{ background: '#ff9800' }}
                                      >
                                        <i className="fas fa-key"></i>
                                        <span className="action-text">PIN</span>
                                      </button>
                                      {admin.is_active ? (
                                        <button 
                                          className="action-btn suspend"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDeactivateAdminModal(admin);
                                            setDeactivationComment('');
                                          }}
                                          title="Suspend Distributor"
                                        >
                                          <i className="fas fa-ban"></i>
                                          <span className="action-text">Suspend</span>
                                        </button>
                                      ) : (
                                        <button 
                                          className="action-btn activate"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm('Are you sure you want to activate this distributor?')) {
                                              handleAdminActivate(admin);
                                            }
                                          }}
                                          title="Activate Distributor"
                                        >
                                          <i className="fas fa-check-circle"></i>
                                          <span className="action-text">Activate</span>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination controls */}
                        {totalPages > 1 && (
                          <div className="pagination-container">
                            <div className="pagination-info">
                              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredAdmins.length)} to {Math.min(currentPage * itemsPerPage, filteredAdmins.length)} of {filteredAdmins.length} distributors
                            </div>
                            <div className="pagination-controls">
                              <button 
                                className="pagination-btn prev" 
                                onClick={goToPrevPage}
                                disabled={currentPage === 1}
                              >
                                <i className="fas fa-chevron-left"></i>
                                <span>Previous</span>
                              </button>
                              
                              <div className="page-numbers">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }
                                  
                                  return (
                                    <button
                                      key={pageNum}
                                      className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                      onClick={() => goToPage(pageNum)}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                                
                                {totalPages > 5 && currentPage < totalPages - 2 && (
                                  <>
                                    <span className="page-dots">...</span>
                                    <button
                                      className={`page-number ${currentPage === totalPages ? 'active' : ''}`}
                                      onClick={() => goToPage(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  </>
                                )}
                              </div>
                              
                              <button 
                                className="pagination-btn next" 
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                              >
                                <span>Next</span>
                                <i className="fas fa-chevron-right"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="empty-state">
                        <i className="fas fa-users-slash"></i>
                        <h3>No Distributors Found</h3>
                        <p>{searchTerm ? 'No distributors match your search. Try different keywords.' : `No ${showDeactivatedAdmins ? 'deactivated' : 'active'} distributors found.`}</p>
                        {searchTerm && (
                          <button className="clear-search-main-btn" onClick={handleClearSearch}>
                            Clear Search
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            ) : activeTab === 'countries' ? (
              <div className="countries-section">
                <div className="section-card smart-table-card">
                  <div className="card-header">
                    <div className="distributor-header">
                      <div className="header-left-section">
                        <h2>{showDeactivatedCountries ? 'Deactivated Countries' : 'All Countries'}</h2>
                        {/* NEW: Toggle for showing deactivated */}
                        <div className="status-toggle">
                          <button 
                            className={`toggle-btn ${!showDeactivatedCountries ? 'active' : ''}`}
                            onClick={() => setShowDeactivatedCountries(false)}
                          >
                            Active ({countries.filter(c => c.is_active).length})
                          </button>
                          <button 
                            className={`toggle-btn ${showDeactivatedCountries ? 'active' : ''}`}
                            onClick={() => setShowDeactivatedCountries(true)}
                          >
                            Deactivated ({countries.filter(c => !c.is_active).length})
                          </button>
                        </div>
                      </div>
                      
                      <div className="header-center-section">
                        <div className="search-section">
                          <div className="search-input-group">
                            <i className="fas fa-search search-icon"></i>
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search country name, code, currency or phone code..."
                              value={countrySearchTerm}
                              onChange={(e) => setCountrySearchTerm(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleCountrySearch()}
                            />
                            {countrySearchTerm && (
                              <button className="clear-search-btn" onClick={handleClearCountrySearch}>
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </div>
                          <button className="search-btn" onClick={handleCountrySearch}>
                            <i className="fas fa-search"></i>
                            <span>Search</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="header-right-section">
                        <div className="distributor-count">
                          <div className="count-icon">
                            <i className="fas fa-globe"></i>
                          </div>
                          <div className="count-details">
                            <div className="count-number">{totalCountries}</div>
                            <div className="count-label">
                              {showDeactivatedCountries ? 'Deactivated' : 'Active'} Countries
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    {countriesLoading ? (
                      <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Loading countries...</span>
                      </div>
                    ) : filteredCountries.length > 0 ? (
                      <>
                        <div className="smart-table-container" ref={countriesTableRef}>
                          <table className="data-table transactions-table smart-table">
                            <thead>
                              <tr>
                                <th className="sticky-col first-col">Country</th>
                                <th>Country Code</th>
                                <th>Currency</th>
                                <th>Currency Name</th>
                                <th>Phone Code</th>
                                <th>Status</th>
                                <th className="text-center sticky-actions">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getCurrentCountries().map((country) => (
                                <tr key={country.id} className={!country.is_active ? 'inactive-row' : ''}>
                                  <td className="sticky-col first-col">
                                    <div className="country-cell">
                                      <span className={`flag-icon flag-${country.country_code?.toLowerCase() || 'us'}`}></span>
                                      <span className="country-name">{country.country_name || 'N/A'}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="code-cell">
                                      <code>{country.country_code || 'N/A'}</code>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="currency-cell">
                                      <span className="currency-symbol">{country.currency_symbol || '$'}</span>
                                      <span className="currency-code">{country.currency_code || 'USD'}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="currency-name-cell">
                                      {country.currency_name || 'N/A'}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="phone-cell">
                                      {country.phone_extension ? `+${country.phone_extension}` : 'N/A'}
                                    </div>
                                  </td>
                                  <td>
                                    <div className={`status-cell ${country.is_active ? 'active' : 'inactive'}`}>
                                      <div className="status-dot"></div>
                                      <span>{country.is_active ? 'Active' : 'Deactivated'}</span>
                                    </div>
                                  </td>
                                  <td className="text-center sticky-actions">
                                    <div className="action-buttons">
                                      <button 
                                        className="action-btn edit"
                                        onClick={() => handleEditCountry(country)}
                                        title="Edit Country"
                                      >
                                        <i className="fas fa-edit"></i>
                                        <span className="action-text">Edit</span>
                                      </button>
                                      {country.is_active ? (
                                        <button 
                                          className="action-btn deactivate"
                                          onClick={() => {
                                            setDeactivateCountryModal(country);
                                            setDeactivationComment('');
                                          }}
                                          title="Deactivate Country"
                                        >
                                          <i className="fas fa-ban"></i>
                                          <span className="action-text">Deactivate</span>
                                        </button>
                                      ) : (
                                        <button 
                                          className="action-btn activate"
                                          onClick={() => {
                                            if (window.confirm('Are you sure you want to activate this country?')) {
                                              handleCountryActivate(country);
                                            }
                                          }}
                                          title="Activate Country"
                                        >
                                          <i className="fas fa-check-circle"></i>
                                          <span className="action-text">Activate</span>
                                        </button>
                                      )}
                                      <button 
                                        className="action-btn delete"
                                        onClick={() => setDeleteCountryTarget(country)}
                                        title="Delete Country"
                                      >
                                        <i className="fas fa-trash"></i>
                                        <span className="action-text">Delete</span>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination controls */}
                        {totalCountryPages > 1 && (
                          <div className="pagination-container">
                            <div className="pagination-info">
                              Showing {Math.min((currentCountryPage - 1) * itemsPerPage + 1, filteredCountries.length)} to {Math.min(currentCountryPage * itemsPerPage, filteredCountries.length)} of {filteredCountries.length} countries
                            </div>
                            <div className="pagination-controls">
                              <button 
                                className="pagination-btn prev" 
                                onClick={goToPrevCountryPage}
                                disabled={currentCountryPage === 1}
                              >
                                <i className="fas fa-chevron-left"></i>
                                <span>Previous</span>
                              </button>
                              
                              <div className="page-numbers">
                                {Array.from({ length: Math.min(5, totalCountryPages) }, (_, i) => {
                                  let pageNum;
                                  if (totalCountryPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentCountryPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentCountryPage >= totalCountryPages - 2) {
                                    pageNum = totalCountryPages - 4 + i;
                                  } else {
                                    pageNum = currentCountryPage - 2 + i;
                                  }
                                  
                                  return (
                                    <button
                                      key={pageNum}
                                      className={`page-number ${currentCountryPage === pageNum ? 'active' : ''}`}
                                      onClick={() => goToCountryPage(pageNum)}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                                
                                {totalCountryPages > 5 && currentCountryPage < totalCountryPages - 2 && (
                                  <>
                                    <span className="page-dots">...</span>
                                    <button
                                      className={`page-number ${currentCountryPage === totalCountryPages ? 'active' : ''}`}
                                      onClick={() => goToCountryPage(totalCountryPages)}
                                    >
                                      {totalCountryPages}
                                    </button>
                                  </>
                                )}
                              </div>
                              
                              <button 
                                className="pagination-btn next" 
                                onClick={goToNextCountryPage}
                                disabled={currentCountryPage === totalCountryPages}
                              >
                                <span>Next</span>
                                <i className="fas fa-chevron-right"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="empty-state">
                        <i className="fas fa-globe"></i>
                        <h3>No Countries Found</h3>
                        <p>{countrySearchTerm ? 'No countries match your search. Try different keywords.' : `No ${showDeactivatedCountries ? 'deactivated' : 'active'} countries found.`}</p>
                        {countrySearchTerm && (
                          <button className="clear-search-main-btn" onClick={handleClearCountrySearch}>
                            Clear Search
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) :  activeTab === 'exchangeRates' ? (
              <div className="exchange-rates-section">
                <div className="section-card smart-table-card">
                  <div className="card-header">
                    <div className="distributor-header">
                      <div className="header-left-section">
                        <h2>Exchange Rates Management</h2>
                      </div>
                      
                      <div className="header-center-section">
                        <div className="search-section">
                          <div className="search-input-group">
                            <i className="fas fa-search search-icon"></i>
                            <input
                              type="text"
                              className="search-input"
                              placeholder="Search country name, currency code..."
                              value={exchangeRateSearchTerm}
                              onChange={(e) => setExchangeRateSearchTerm(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleExchangeRateSearch()}
                            />
                            {exchangeRateSearchTerm && (
                              <button className="clear-search-btn" onClick={handleClearExchangeRateSearch}>
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </div>
                          <button className="search-btn" onClick={handleExchangeRateSearch}>
                            <i className="fas fa-search"></i>
                            <span>Search</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="header-right-section">
                        <div className="distributor-count">
                          <div className="count-icon">
                            <i className="fas fa-coins"></i>
                          </div>
                          <div className="count-details">
                            <div className="count-number">{totalExchangeRates}</div>
                            <div className="count-label">Exchange Rates</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    {exchangeRatesLoading ? (
                      <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Loading exchange rates...</span>
                      </div>
                    ) : filteredExchangeRates.length > 0 ? (
                      <>
                        <div className="smart-table-container" ref={exchangeRatesTableRef}>
                          <table className="data-table transactions-table smart-table">
                            <thead>
                              <tr>
                                <th className="sticky-col first-col">Country</th>
                                <th>Currency</th>
                                <th className="text-center">1 USD = (Local Units)</th>
                                <th>Last Updated</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getCurrentExchangeRates().map((rate) => (
                                <tr key={rate.id}>
                                  <td className="sticky-col first-col">
                                    <div className="country-cell">
                                      <span className={`flag-icon flag-${rate.country_code?.toLowerCase() || 'us'}`}></span>
                                      <span className="country-name">{rate.country_name || 'N/A'}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="currency-cell">
                                      <span className="currency-symbol">{rate.currency_symbol || '$'}</span>
                                      <span className="currency-code">{rate.currency_code || 'USD'}</span>
                                      <div className="currency-name-small">{rate.currency_name || 'N/A'}</div>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <div className="rate-input-cell">
                                      <div className="rate-label">1 USD = ? {rate.currency_code}</div>
                                      <div className="rate-input-group">
                                        <span className="rate-currency">$1 =</span>
                                        <input
                                          type="number"
                                          className="rate-input"
                                          step="0.01"
                                          min="0"
                                          value={editingRates[rate.id]?.usd_to_local_rate ?? rate.usd_to_local_rate ?? ''}
                                          onChange={(e) => handleRateChange(rate.id, 'usd_to_local_rate', e.target.value)}
                                          placeholder={rate.currency_code === 'INR' ? '85' : '1'}
                                        />
                                        <span className="rate-currency">{rate.currency_code}</span>
                                      </div>
                                      <div className="rate-note">1 USD = {editingRates[rate.id]?.usd_to_local_rate ?? rate.usd_to_local_rate ?? 0} {rate.currency_code}</div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="date-cell">
                                      {rate.updated_at ? formatDateTime(rate.updated_at) : 'Never'}
                                    </div>
                                  </td>
                                  <td>
                                    <div className={`status-cell ${rate.is_active ? 'active' : 'inactive'}`}>
                                      <div className="status-dot"></div>
                                      <span>{rate.is_active ? 'Active' : 'Inactive'}</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Pagination controls */}
                        {totalExchangeRatePages > 1 && (
                          <div className="pagination-container">
                            <div className="pagination-info">
                              Showing {Math.min((currentExchangeRatePage - 1) * itemsPerPage + 1, filteredExchangeRates.length)} to {Math.min(currentExchangeRatePage * itemsPerPage, filteredExchangeRates.length)} of {filteredExchangeRates.length} rates
                            </div>
                            <div className="pagination-controls">
                              <button 
                                className="pagination-btn prev" 
                                onClick={goToPrevExchangeRatePage}
                                disabled={currentExchangeRatePage === 1}
                              >
                                <i className="fas fa-chevron-left"></i>
                                <span>Previous</span>
                              </button>
                              
                              <div className="page-numbers">
                                {Array.from({ length: Math.min(5, totalExchangeRatePages) }, (_, i) => {
                                  let pageNum;
                                  if (totalExchangeRatePages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentExchangeRatePage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentExchangeRatePage >= totalExchangeRatePages - 2) {
                                    pageNum = totalExchangeRatePages - 4 + i;
                                  } else {
                                    pageNum = currentExchangeRatePage - 2 + i;
                                  }
                                  
                                  return (
                                    <button
                                      key={pageNum}
                                      className={`page-number ${currentExchangeRatePage === pageNum ? 'active' : ''}`}
                                      onClick={() => goToExchangeRatePage(pageNum)}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                                
                                {totalExchangeRatePages > 5 && currentExchangeRatePage < totalExchangeRatePages - 2 && (
                                  <>
                                    <span className="page-dots">...</span>
                                    <button
                                      className={`page-number ${currentExchangeRatePage === totalExchangeRatePages ? 'active' : ''}`}
                                      onClick={() => goToExchangeRatePage(totalExchangeRatePages)}
                                    >
                                      {totalExchangeRatePages}
                                    </button>
                                  </>
                                )}
                              </div>
                              
                              <button 
                                className="pagination-btn next" 
                                onClick={goToNextExchangeRatePage}
                                disabled={currentExchangeRatePage === totalExchangeRatePages}
                              >
                                <span>Next</span>
                                <i className="fas fa-chevron-right"></i>
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* Update All Button at bottom */}
                        <div className="update-all-section">
                          <button 
                            className="update-all-btn"
                            onClick={updateAllRates}
                            disabled={isUpdatingRates || exchangeRatesLoading}
                          >
                            {isUpdatingRates ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Updating All Rates...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save"></i>
                                Update All Exchange Rates
                              </>
                            )}
                          </button>
                          <div className="update-note">
                            <i className="fas fa-info-circle"></i>
                            <span>Note: This will update all rates in the database. Distributors will use these rates for currency conversions.</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="empty-state">
                        <i className="fas fa-chart-line"></i>
                        <h3>No Exchange Rates Found</h3>
                        <p>{exchangeRateSearchTerm ? 'No exchange rates match your search. Try different keywords.' : 'No exchange rates available.'}</p>
                        {exchangeRateSearchTerm && (
                          <button className="clear-search-main-btn" onClick={handleClearExchangeRateSearch}>
                            Clear Search
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : activeTab === 'fairPlay' ? (
              <FairPlay superAdmin />
            ) : activeTab === 'promotions' ? (
              <Promotions global />
            ) : activeTab === 'commission' ? (
              <CommissionSharing />
            ) : activeTab === 'tournaments' ? (
              <TournamentManagement />
            ) : activeTab === 'testUsers' ? (
              <CreateTestUser endpoint="/api/super-admin/create-test-user" />
            ) :
            (
              <div className="transactions-section">
                {/* FILTERS CARD */}
                <div className="section-card">
                  <div className="card-header">
                    <h2>Filter Transactions</h2>
                    <button className="reset-btn" onClick={handleResetFilters}>
                      <i className="fas fa-redo"></i>
                      <span>Reset Filters</span>
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="filters-grid">
                      <div className="filter-group">
                        <label>Transaction Type</label>
                        <select name="type" value={filters.type} onChange={handleFilterChange}>
                          <option value="">All Types</option>
                          <option value="deposit">Deposit</option>
                          <option value="withdrawal">Withdrawal</option>
                          <option value="admin_deposit">Admin Deposit</option>
                          <option value="admin_withdrawal">Admin Withdrawal</option>
                          <option value="game_entry">Game Entry</option>
                          <option value="winning">Winning</option>
                        </select>
                      </div>

                      <div className="filter-group">
                        <label>Currency</label>
                        <select name="currency" value={filters.currency} onChange={handleFilterChange}>
                          <option value="">All Currencies</option>
                          <option value="INR">INR</option>
                          <option value="USD">USD</option>
                          <option value="LRD">LRD</option>
                          <option value="XOF">XOF (CFA)</option>
                        </select>
                      </div>

                      <div className="filter-group">
                        <label>Status</label>
                        <select name="status" value={filters.status} onChange={handleFilterChange}>
                          <option value="">All Status</option>
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>

                      <div className="filter-group">
                        <label>Username</label>
                        <input 
                          type="text" 
                          name="username" 
                          placeholder="Enter username"
                          value={filters.username}
                          onChange={handleFilterChange}
                        />
                      </div>

                      <div className="filter-group">
                        <label>Email</label>
                        <input 
                          type="email" 
                          name="email" 
                          placeholder="Enter email"
                          value={filters.email}
                          onChange={handleFilterChange}
                        />
                      </div>

                      <div className="filter-group">
                        <label>Date Range</label>
                        <div className="date-range-inputs">
                          <input 
                            type="date" 
                            name="startDate" 
                            value={filters.startDate}
                            onChange={handleFilterChange}
                          />
                          <span className="date-separator">to</span>
                          <input 
                            type="date" 
                            name="endDate" 
                            value={filters.endDate}
                            onChange={handleFilterChange}
                          />
                        </div>
                      </div>

                      <div className="filter-group full-width">
                        <button className="apply-filters-btn" onClick={handleFilterSubmit}>
                          <i className="fas fa-filter"></i>
                          <span>Apply Filters</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* TRANSACTIONS TABLE */}
                <div className="section-card smart-table-card">
                  <div className="card-header">
                    <h2>Transaction History</h2>
                    <div className="total-count">
                      {txnLoading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        `${transactions.length} Transaction${transactions.length !== 1 ? 's' : ''}`
                      )}
                    </div>
                  </div>
                  <div className="card-body">
                    {txnLoading ? (
                      <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Loading transactions...</span>
                      </div>
                    ) : transactions.length > 0 ? (
                      <div className="smart-table-container" ref={transactionsTableRef}>
                        <table className="data-table smart-table">
                          <thead>
                            <tr>
                              <th className="sticky-col first-col">Date & Time</th>
                              <th className="sticky-col second-col">Transaction ID</th>
                              <th>User</th>
                              <th>Type</th>
                              <th className="text-right">Amount</th>
                              <th className="text-right">Opening Balance</th>
                              <th className="text-right">Closing Balance</th>
                              <th>Status</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((tx) => (
                              <tr key={tx.id}>
                                <td className="sticky-col first-col">
                                  <div className="date-time-cell">
                                    {formatDateTime(tx.created_at)}
                                  </div>
                                </td>
                                <td className="sticky-col second-col">
                                  <div className="tx-id-cell">
                                    {tx.reference_number}
                                  </div>
                                </td>
                                <td>
                                  <div className="user-info-cell">
                                    <div>
                                      <div className="username">{tx.username}</div>
                                      <div className="user-email">{tx.user_email}</div>
                                      {tx.user_id && (
                                        <div className="user-id">ID: {tx.user_id}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className={`tx-type ${tx.type}`}>
                                    {tx.type.replace('_', ' ').toUpperCase()}
                                  </span>
                                </td>
                                <td className="text-right">
                                  <div className={`amount-cell ${tx.amount >= 0 ? 'credit' : 'debit'}`}>
                                    {formatCurrency(Math.abs(tx.amount), tx.currency)}
                                    {tx.amount >= 0 ? (
                                      <i className="fas fa-arrow-up credit-arrow"></i>
                                    ) : (
                                      <i className="fas fa-arrow-down debit-arrow"></i>
                                    )}
                                  </div>
                                </td>
                                <td className="text-right">
                                  <div className="balance-cell">
                                    {formatCurrency(tx.opening_balance, tx.currency)}
                                  </div>
                                </td>
                                <td className="text-right">
                                  <div className="balance-cell">
                                    {formatCurrency(tx.closing_balance, tx.currency)}
                                  </div>
                                </td>
                                <td>
                                  <span className={`status-badge ${tx.status}`}>
                                    {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                                  </span>
                                </td>
                                <td>
                                  <div className="description-cell" title={tx.description}>
                                    {tx.description || 'N/A'}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <i className="fas fa-exchange-alt"></i>
                        <h3>No Transactions Found</h3>
                        <p>Try adjusting your filters or check back later</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODALS */}
      {showCreateModal && (
        <CreateDistributorModal 
          onClose={() => setShowCreateModal(false)} 
          refresh={fetchAdmins} 
          authHeader={getAuthHeader}
          availableCountries={countries.filter(c => c.is_active)}
          availableAdmins={admins.filter(a => !a.parent_admin_id)}
        />
      )}

      {showCountryModal && (
        <CountryModal 
          form={countryForm}
          setForm={setCountryForm}
          onSubmit={handleCountrySubmit}
          onClose={() => {
            setShowCountryModal(false);
            resetCountryForm();
          }}
          editingCountry={editingCountry}
        />
      )}

      {/* NEW: Edit Distributor Modal */}
      {showEditDistributorModal && editingDistributor && (
        <EditDistributorModal 
          distributor={editingDistributor}
          form={distributorEditForm}
          setForm={setDistributorEditForm}
          onSubmit={handleEditDistributor}
          onClose={() => {
            setShowEditDistributorModal(false);
            setEditingDistributor(null);
          }}
          availableCountries={countries.filter(c => c.is_active)}
        />
      )}

      {/* NEW: Deactivate Admin Modal */}
      {deactivateAdminModal && (
        <DeactivateModal 
          type="distributor"
          item={deactivateAdminModal}
          comment={deactivationComment}
          setComment={setDeactivationComment}
          onConfirm={handleAdminDeactivate}
          onClose={() => {
            setDeactivateAdminModal(null);
            setDeactivationComment('');
          }}
        />
      )}

      {/* NEW: Deactivate Country Modal */}
      {deactivateCountryModal && (
        <DeactivateModal 
          type="country"
          item={deactivateCountryModal}
          comment={deactivationComment}
          setComment={setDeactivationComment}
          onConfirm={handleCountryDeactivate}
          onClose={() => {
            setDeactivateCountryModal(null);
            setDeactivationComment('');
          }}
        />
      )}

      {fundingTarget && (
        <FundAdminModal 
          admin={fundingTarget} 
          onClose={() => setFundingTarget(null)} 
          refresh={fetchAdmins} 
          authHeader={getAuthHeader} 
        />
      )}

      {deleteTarget && (
        <DeleteAdminModal 
          admin={deleteTarget} 
          onClose={() => setDeleteTarget(null)} 
          onConfirm={handleDeleteConfirm} 
        />
      )}

      {deleteCountryTarget && (
        <DeleteCountryModal 
          country={deleteCountryTarget} 
          onClose={() => setDeleteCountryTarget(null)} 
          onConfirm={handleCountryDeleteConfirm} 
        />
      )}

      {/* QUICK VIEW PANEL */}
      {showQuickView && quickViewData && (
        <div className="quick-view-panel">
          <div className="quick-view-header">
            <h3><i className="fas fa-user-tie"></i> Distributor Details</h3>
            <button className="modal-close" onClick={() => setShowQuickView(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="quick-view-content">
            <div className="detail-section">
              <h4>Basic Information</h4>
              <div className="detail-item">
                <span className="detail-label">Username:</span>
                <span className="detail-value">{quickViewData.username}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{quickViewData.email}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Country:</span>
                <span className="detail-value">{quickViewData.country}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Currency:</span>
                <span className="detail-value">
                  {quickViewData.currency_code} ({quickViewData.currency_name})
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created Date:</span>
                <span className="detail-value">{formatDate(quickViewData.created_at)}</span>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Financial Summary</h4>
              <div className="detail-item">
                <span className="detail-label">USD Balance:</span>
                <span className="detail-value">${quickViewData.wallet_balance_usd.toFixed(2)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">INR Balance:</span>
                <span className="detail-value">₹{quickViewData.wallet_balance_inr.toFixed(2)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Local Balance:</span>
                <span className="detail-value">
                  {quickViewData.local_currency_symbol}
                  {quickViewData.local_balance.toFixed(2)}
                  <span className="currency-code-small"> ({quickViewData.local_currency})</span>
                </span>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Contact Info</h4>
              <div className="detail-item">
                <span className="detail-label">Primary Phone:</span>
                <span className="detail-value">
                  {quickViewData.phone_extension ? `+${quickViewData.phone_extension} ` : ''}
                  {quickViewData.primary_phone || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Secondary Phone:</span>
                <span className="detail-value">
                  {quickViewData.phone_extension ? `+${quickViewData.phone_extension} ` : ''}
                  {quickViewData.secondary_phone || 'N/A'}
                </span>
              </div>
            </div>
            
            <div className="quick-view-actions">
              <button 
                className="btn-primary"
                onClick={() => {
                  setFundingTarget(quickViewData);
                  setShowQuickView(false);
                }}
              >
                <i className="fas fa-coins"></i>
                Fund Now
              </button>
              <button 
                className="btn-secondary"
                onClick={() => setShowQuickView(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MODAL COMPONENTS ====================

// Country Modal Component
const CountryModal = ({ form, setForm, onSubmit, onClose, editingCountry }) => {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal country-modal">
        <div className="modal-header">
          <h3>
            <i className="fas fa-globe"></i>
            {editingCountry ? 'Edit Country' : 'Add New Country'}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <form onSubmit={onSubmit} className="country-form">
            <div className="form-section">
              <h4>Basic Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Country Name *</label>
                  <input 
                    type="text" 
                    name="country_name"
                    placeholder="Enter country name"
                    value={form.country_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Country Code *</label>
                  <input 
                    type="text" 
                    name="country_code"
                    placeholder="e.g., US, IN, LR"
                    value={form.country_code}
                    onChange={handleChange}
                    required
                    maxLength="2"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Currency Code *</label>
                  <input 
                    type="text" 
                    name="currency_code"
                    placeholder="e.g., USD, INR, LRD"
                    value={form.currency_code}
                    onChange={handleChange}
                    required
                    maxLength="3"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>

                <div className="form-group">
                  <label>Currency Name *</label>
                  <input 
                    type="text" 
                    name="currency_name"
                    placeholder="e.g., US Dollar, Indian Rupee, Liberian Dollar"
                    value={form.currency_name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Currency Symbol</label>
                  <input 
                    type="text" 
                    name="currency_symbol"
                    placeholder="e.g., $, ₹, L$"
                    value={form.currency_symbol}
                    onChange={handleChange}
                    maxLength="3"
                  />
                </div>

                <div className="form-group">
                  <label>Phone Code (Extension)</label>
                  <input 
                    type="text" 
                    name="phone_extension"
                    placeholder="e.g., 1, 91, 231"
                    value={form.phone_extension}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                  />
                  <span>Active Country (Available for selection)</span>
                </label>
              </div>
            </div>
          </form>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onSubmit}>
            <i className="fas fa-save"></i>
            {editingCountry ? 'Update Country' : 'Save Country'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Deactivate Modal Component
const DeactivateModal = ({ type, item, comment, setComment, onConfirm, onClose }) => {
  const handleSubmit = () => {
    if (type === 'distributor' && !comment.trim()) {
      if (!window.confirm('Are you sure you want to suspend this distributor without providing a reason?')) {
        return;
      }
    } else if (type === 'country' && !comment.trim()) {
      if (!window.confirm('Are you sure you want to deactivate this country without providing a reason?')) {
        return;
      }
    }
    onConfirm();
  };

  return (
    <div className="modal-overlay">
      <div className="modal deactivate-modal">
        <div className="modal-header">
          <h3>
            <i className="fas fa-exclamation-triangle"></i>
            {type === 'distributor' ? 'Suspend Distributor' : 'Deactivate Country'}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="warning-section">
            <div className="warning-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="warning-content">
              <p>
                Are you sure you want to {type === 'distributor' ? 'suspend' : 'deactivate'} 
                <strong> {type === 'distributor' ? item.username : item.country_name}</strong>?
              </p>
            </div>
          </div>
          
          <div className="form-section">
            <div className="form-group">
              <label>Reason for {type === 'distributor' ? 'suspension' : 'deactivation'} (optional):</label>
              <textarea 
                className="deactivation-comment"
                placeholder={`Enter reason for ${type === 'distributor' ? 'suspension' : 'deactivation'}...`}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows="3"
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={handleSubmit}>
            <i className="fas fa-ban"></i>
            {type === 'distributor' ? 'Suspend' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Distributor Modal Component
const EditDistributorModal = ({ distributor, form, setForm, onSubmit, onClose, availableCountries }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryChange = (countryId) => {
    const country = availableCountries.find(c => c.id === parseInt(countryId));
    if (country) {
      setForm(prev => ({
        ...prev,
        country_id: country.id
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const selectedCountry = availableCountries.find(c => c.id === parseInt(form.country_id));

  return (
    <div className="modal-overlay">
      <div className="modal create-distributor-modal">
        <div className="modal-header">
          <h3>Edit Distributor: {distributor.username}</h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="distributor-form">
            <div className="form-section">
              <h4>Basic Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Username *</label>
                  <input 
                    type="text" 
                    name="username"
                    placeholder="Enter username"
                    value={form.username}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Email *</label>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Enter email address"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Country/Region *</label>
                  <select 
                    name="country_id"
                    value={form.country_id}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    required
                  >
                    <option value="">Select Country</option>
                    {availableCountries.map(country => (
                      <option key={country.id} value={country.id}>
                        {country.country_name} ({country.country_code}) - {country.currency_code} {country.currency_symbol}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedCountry && (
                <div className="country-info-preview">
                  <div className="info-row">
                    <span className="info-label">Country:</span>
                    <span className="info-value">{selectedCountry.country_name} ({selectedCountry.country_code})</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Currency:</span>
                    <span className="info-value">
                      {selectedCountry.currency_code} ({selectedCountry.currency_name}) {selectedCountry.currency_symbol}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Phone Code:</span>
                    <span className="info-value">
                      {selectedCountry.phone_extension ? `+${selectedCountry.phone_extension}` : 'Not set'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <h4>Contact Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Primary Phone *</label>
                  <input 
                    type="tel" 
                    name="primary_phone"
                    placeholder="Enter phone number"
                    value={form.primary_phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Secondary Phone</label>
                  <input 
                    type="tel" 
                    name="secondary_phone"
                    placeholder="Optional"
                    value={form.secondary_phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <PermissionSelector value={form.permissions} onChange={(perms) => setForm({ ...form, permissions: perms })} />

            <div className="form-section">
              <h4>Banking & Payment Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input 
                    type="text" 
                    name="bank_name"
                    placeholder="E.g., Ecobank, UBA, GTBank"
                    value={form.bank_name}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label>Account Number</label>
                  <input 
                    type="text" 
                    name="account_number"
                    placeholder="Bank account number"
                    value={form.account_number}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>SWIFT Code</label>
                  <input 
                    type="text" 
                    name="swift_code"
                    placeholder="E.g., ECOCLRML"
                    value={form.swift_code}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label>Mobile Money Number</label>
                  <input 
                    type="text" 
                    name="mobile_money"
                    placeholder="E.g., 0771234567 (Orange/Lonestar)"
                    value={form.mobile_money}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Google Pay / PhonePe ID</label>
                  <input 
                    type="text" 
                    name="gpay"
                    placeholder="GPay, PhonePe, Paytm, etc."
                    value={form.gpay}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label>Other Payment Info</label>
                  <input 
                    type="text" 
                    name="other_payment_info"
                    placeholder="Any other payment methods"
                    value={form.other_payment_info}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSubmit}>
            <i className="fas fa-save"></i>
            Update Distributor
          </button>
        </div>
      </div>
    </div>
  );
};

// CreateDistributorModal
const CreateDistributorModal = ({ onClose, refresh, authHeader, availableCountries, availableAdmins }) => {
  const [form, setForm] = useState({ 
    username: '', 
    email: '', 
    password: '',
    country_id: '',
    parent_admin_id: '',
    primary_phone: '',
    secondary_phone: '',
    bank_name: '',
    account_number: '',
    swift_code: '',
    mobile_money: '',
    gpay: '',
    other_payment_info: '',
    permissions: ['full']
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const handleCountryChange = (countryId) => {
    const country = availableCountries.find(c => c.id === parseInt(countryId));
    if (country) {
      setSelectedCountry(country);
      setForm(prev => ({
        ...prev,
        country_id: country.id
      }));
    }
  };

  const handleParentChange = (parentId) => {
    if (!parentId) {
      setForm(prev => ({ ...prev, parent_admin_id: '' }));
      return;
    }
    const parent = availableAdmins.find(a => String(a.id) === String(parentId));
    if (parent) {
      const country = availableCountries.find(c => String(c.id) === String(parent.country_id));
      if (country) setSelectedCountry(country);
      setForm(prev => ({
        ...prev,
        parent_admin_id: parent.id,
        country_id: parent.country_id || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (!form.country_id) {
      setError('Please select a country');
      setLoading(false);
      return;
    }

    if (!form.primary_phone) {
      setError('Primary phone number is required');
      setLoading(false);
      return;
    }

    const distributorData = {
      username: form.username,
      email: form.email,
      password: form.password,
      country_id: form.country_id,
      primary_phone: form.primary_phone,
      secondary_phone: form.secondary_phone || '',
      bank_name: form.bank_name || '',
      account_number: form.account_number || '',
      swift_code: form.swift_code || '',
      mobile_money: form.mobile_money || '',
      gpay: form.gpay || '',
      other_payment_info: form.other_payment_info || '',
      parent_admin_id: form.parent_admin_id || null,
      permissions: form.permissions
    };

    console.log('📤 Sending distributor data:', distributorData);
    
    try {
      const response = await axios.post(
        `${API_BASE}/api/super-admin/create-distributor`, 
        distributorData, 
        authHeader()
      );
      console.log('✅ Create distributor response:', response.data);
      refresh();
      onClose();
    } catch (err) {
      console.error('❌ Create distributor error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to create distributor. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal create-distributor-modal">
        <div className="modal-header">
          <h3>Create New Distributor</h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="distributor-form">
            <div className="form-section">
              <h4>Basic Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Username *</label>
                  <input 
                    type="text" 
                    placeholder="Enter username"
                    value={form.username}
                    onChange={(e) => setForm({...form, username: e.target.value})}
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Email *</label>
                  <input 
                    type="email" 
                    placeholder="Enter email address"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Parent Distributor (Main Account - optional)</label>
                  <select
                    value={form.parent_admin_id || ''}
                    onChange={(e) => handleParentChange(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">None (Standalone Account)</option>
                    {availableAdmins.map(admin => (
                      <option key={admin.id} value={admin.id}>
                        {admin.username} ({admin.country})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input 
                    type="password" 
                    placeholder="Minimum 8 characters"
                    value={form.password}
                    onChange={(e) => setForm({...form, password: e.target.value})}
                    required
                    minLength="8"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>Country/Region *</label>
                  <select 
                    value={form.country_id}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    disabled={loading || !!form.parent_admin_id}
                    required
                  >
                    <option value="">Select Country</option>
                    {availableCountries.map(country => (
                      <option key={country.id} value={country.id}>
                        {country.country_name} ({country.country_code}) - {country.currency_code} {country.currency_symbol}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedCountry && (
                <div className="country-info-preview">
                  <div className="info-row">
                    <span className="info-label">Country:</span>
                    <span className="info-value">{selectedCountry.country_name} ({selectedCountry.country_code})</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Currency:</span>
                    <span className="info-value">
                      {selectedCountry.currency_code} ({selectedCountry.currency_name}) {selectedCountry.currency_symbol}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Phone Code:</span>
                    <span className="info-value">
                      {selectedCountry.phone_extension ? `+${selectedCountry.phone_extension}` : 'Not set'}
                    </span>
                  </div>
                  <div className="info-note">
                    <i className="fas fa-info-circle"></i>
                    <span>{form.parent_admin_id ? 'Country inherited from parent account' : "Note: This distributor will be linked to this country's settings"}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <h4>Contact Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Primary Phone *</label>
                  <input 
                    type="tel" 
                    placeholder="Enter phone number"
                    value={form.primary_phone}
                    onChange={(e) => setForm({...form, primary_phone: e.target.value})}
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Secondary Phone</label>
                  <input 
                    type="tel" 
                    placeholder="Optional"
                    value={form.secondary_phone}
                    onChange={(e) => setForm({...form, secondary_phone: e.target.value})}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <PermissionSelector value={form.permissions} onChange={(perms) => setForm({ ...form, permissions: perms })} disabled={loading} />

            <div className="form-section">
              <h4>Banking & Payment Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input 
                    type="text" 
                    placeholder="E.g., Ecobank, UBA, GTBank"
                    value={form.bank_name}
                    onChange={(e) => setForm({...form, bank_name: e.target.value})}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Account Number</label>
                  <input 
                    type="text" 
                    placeholder="Bank account number"
                    value={form.account_number}
                    onChange={(e) => setForm({...form, account_number: e.target.value})}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>SWIFT Code</label>
                  <input 
                    type="text" 
                    placeholder="E.g., ECOCLRML"
                    value={form.swift_code}
                    onChange={(e) => setForm({...form, swift_code: e.target.value})}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Mobile Money Number</label>
                  <input 
                    type="text" 
                    placeholder="E.g., 0771234567 (Orange/Lonestar)"
                    value={form.mobile_money}
                    onChange={(e) => setForm({...form, mobile_money: e.target.value})}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Google Pay / PhonePe ID</label>
                  <input 
                    type="text" 
                    placeholder="GPay, PhonePe, Paytm, etc."
                    value={form.gpay}
                    onChange={(e) => setForm({...form, gpay: e.target.value})}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Other Payment Info</label>
                  <input 
                    type="text" 
                    placeholder="Any other payment methods"
                    value={form.other_payment_info}
                    onChange={(e) => setForm({...form, other_payment_info: e.target.value})}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Creating...
              </>
            ) : (
              'Create Distributor'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// FundAdminModal 
const FundAdminModal = ({ admin, onClose, refresh, authHeader }) => {
  const [form, setForm] = useState({ amount: '', currency: 'USD', action: 'credit' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/api/super-admin/fund-admin`, {
        targetAdminId: admin.id,
        ...form
      }, authHeader());
      
      if (response.data.success) {
        refresh();
        onClose();
      } else {
        setError(response.data.message || 'Transaction failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentBalance = () => {
    const curr = form.currency.toLowerCase();
    const balance = admin[`wallet_balance_${curr}`] || 0;
    return parseFloat(balance).toFixed(2);
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Manage Wallet</h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="admin-info-modal">
            <div className="admin-avatar-large">
              {admin.username?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="admin-details">
              <div className="admin-name">{admin.username}</div>
              <div className="admin-email">{admin.email}</div>
              {admin.country && (
                <div className="admin-country">
                  <i className="fas fa-globe"></i>
                  {admin.country}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          <div className="current-balance-display">
            <div className="balance-label">Current {form.currency} Balance</div>
            <div className="balance-amount">
              {form.currency === 'INR' ? '₹' : 
               form.currency === 'USD' ? '$' : 
               form.currency === 'LRD' ? 'L$' : 
               form.currency === 'XOF' ? 'CFA' : ''}
              {getCurrentBalance()}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Transaction Type</label>
              <div className="action-toggle">
                <button 
                  type="button"
                  className={`toggle-btn ${form.action === 'credit' ? 'active credit' : ''}`}
                  onClick={() => setForm({...form, action: 'credit'})}
                  disabled={loading}
                >
                  <i className="fas fa-plus-circle"></i>
                  Credit (+)
                </button>
                <button 
                  type="button"
                  className={`toggle-btn ${form.action === 'debit' ? 'active debit' : ''}`}
                  onClick={() => setForm({...form, action: 'debit'})}
                  disabled={loading}
                >
                  <i className="fas fa-minus-circle"></i>
                  Debit (-)
                </button>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Currency</label>
                <select 
                  value={form.currency}
                  onChange={(e) => setForm({...form, currency: e.target.value})}
                  disabled={loading}
                >
                  <option value="USD">USD ($)</option>
                  <option value="INR">INR (₹)</option>
                  <option value="LRD">LRD (L$)</option>
                  <option value="XOF">XOF (CFA)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Amount</label>
                <input 
                  type="number" 
                  placeholder="Enter amount"
                  value={form.amount}
                  onChange={(e) => setForm({...form, amount: e.target.value})}
                  required
                  min="0.01"
                  step="0.01"
                  disabled={loading}
                />
              </div>
            </div>
          </form>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button 
            className={`btn-action ${form.action === 'credit' ? 'btn-success' : 'btn-danger'}`}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Processing...
              </>
            ) : form.action === 'credit' ? (
              <>
                <i className="fas fa-check-circle"></i>
                Confirm Credit
              </>
            ) : (
              <>
                <i className="fas fa-exclamation-triangle"></i>
                Confirm Debit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// DeleteAdminModal
const DeleteAdminModal = ({ admin, onClose, onConfirm }) => {
  return (
    <div className="modal-overlay">
      <div className="modal modal-danger">
        <div className="modal-header">
          <h3><i className="fas fa-exclamation-triangle"></i> Delete Distributor</h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="warning-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <div className="warning-content">
            <p>Are you sure you want to permanently delete this distributor?</p>
            <div className="admin-to-delete">
              <div className="admin-avatar">{admin.username?.[0]?.toUpperCase() || 'A'}</div>
              <div className="admin-details">
                <div className="admin-name">{admin.username}</div>
                <div className="admin-email">{admin.email}</div>
                {admin.country && (
                  <div className="admin-country">
                    <i className="fas fa-map-marker-alt"></i>
                    {admin.country}
                  </div>
                )}
              </div>
            </div>
            <div className="warning-note">
              <i className="fas fa-info-circle"></i>
              <span>This action cannot be undone. All data will be permanently deleted.</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm}>
            <i className="fas fa-trash"></i>
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
};

// DeleteCountryModal
const DeleteCountryModal = ({ country, onClose, onConfirm }) => {
  return (
    <div className="modal-overlay">
      <div className="modal modal-danger">
        <div className="modal-header">
          <h3><i className="fas fa-exclamation-triangle"></i> Delete Country</h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="warning-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <div className="warning-content">
            <p>Are you sure you want to permanently delete this country?</p>
            <div className="admin-to-delete">
              <div className="country-flag">
                <i className="fas fa-globe"></i>
              </div>
              <div className="admin-details">
                <div className="admin-name">{country.country_name}</div>
                <div className="admin-email">Code: {country.country_code}</div>
                <div className="admin-country">
                  <i className="fas fa-money-bill"></i>
                  {country.currency_code} ({country.currency_name})
                </div>
              </div>
            </div>
            <div className="warning-note">
              <i className="fas fa-info-circle"></i>
              <span>Warning: This will affect all distributors from this country. This action cannot be undone.</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm}>
            <i className="fas fa-trash"></i>
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;